import React, { useState, useCallback } from 'react';
import { View, FlatList, TouchableOpacity, RefreshControl, ActivityIndicator, ScrollView } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { useFocusEffect } from '@react-navigation/native';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import SvgIcon from '../../../../components/Icon/Icon';
import api from '../../../../utils/api';

import NewTicketModal from './NewTicketModal';


type TabKey = 'assigned' | 'reported';

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'Todo' },
  { value: 'nuevo', label: 'Nuevos' },
  { value: 'en_seguimiento', label: 'En seguimiento' },
  { value: 'atendida', label: 'Atendidas' },
];


function parseLocation(description?: string | null): { location: string | null; body: string } {
  if (!description) return { location: null, body: '' };
  const match = description.match(/^📍\s*(.+?)\n\n([\s\S]*)$/);
  if (match) return { location: match[1].trim(), body: match[2].trim() };
  return { location: null, body: description };
}


export default function GuardTicketsScreen({ route, navigation }: any) {
  const { communityId } = route.params ?? {};
  const [tab, setTab] = useState<TabKey>('assigned');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showNew, setShowNew] = useState(false);

  const ticketsQuery = useQuery<any>({
    queryKey: ['guard.tickets.list', communityId, tab, statusFilter],
    queryFn: () => {
      const params: any = { per_page: 50 };
      if (tab === 'reported') params.reporter = 'mine';
      if (statusFilter !== 'all') params.status = statusFilter;
      return api.get(`/communities/${communityId}/tickets`, { params }).then(r => r.data);
    },
    enabled: !!communityId,
  });

  // Contadores de las dos pestañas siempre visibles (como Acceso): total paginado de cada
  // una con el filtro de status actual, sin traer las filas.
  const countParams = (reporter?: string) => {
    const params: any = { per_page: 1 };
    if (reporter) params.reporter = reporter;
    if (statusFilter !== 'all') params.status = statusFilter;
    return params;
  };
  const assignedCountQuery = useQuery<number>({
    queryKey: ['guard.tickets.count', communityId, 'assigned', statusFilter],
    queryFn: () => api.get(`/communities/${communityId}/tickets`, { params: countParams() }).then(r => r.data?.total ?? 0),
    enabled: !!communityId,
  });
  const reportedCountQuery = useQuery<number>({
    queryKey: ['guard.tickets.count', communityId, 'reported', statusFilter],
    queryFn: () => api.get(`/communities/${communityId}/tickets`, { params: countParams('mine') }).then(r => r.data?.total ?? 0),
    enabled: !!communityId,
  });

  useFocusEffect(useCallback(() => {
    ticketsQuery.refetch();
    assignedCountQuery.refetch();
    reportedCountQuery.refetch();
  }, [communityId, tab, statusFilter]));

  const tickets: any[] = ticketsQuery.data?.data ?? [];

  const counts = {
    assigned: assignedCountQuery.data ?? 0,
    reported: reportedCountQuery.data ?? 0,
  };

  return (
    // Fondo gris y el contenido en una card, como Registro y Acceso.
    <View style={tw`flex-1 bg-background`}>
      <SafeAreaView edges={['top']}>
        <View style={tw`items-center px-5 mt-3 mb-4`}>
          <Text style={tw`text-xl`}>Incidencias</Text>
        </View>

        {/* Tabs Asignadas / Reportadas */}
        <View style={tw`row px-5 gap-3 mb-3`}>
          <TabPill
            label="Asignadas"
            count={counts.assigned}
            active={tab === 'assigned'}
            onPress={() => setTab('assigned')}
          />
          <TabPill
            label="Reportadas"
            count={counts.reported}
            active={tab === 'reported'}
            onPress={() => setTab('reported')}
          />
        </View>

        {/* Filtro de status */}
        <View style={tw`pb-3`}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={tw`gap-2 px-5`}>
            {STATUS_FILTERS.map(f => (
              <TouchableOpacity
                key={f.value}
                onPress={() => setStatusFilter(f.value)}
                style={tw.style(
                  `px-4 py-2 rounded-full border`,
                  // Seleccionado: verde un poco más oscuro que el turquesa (primary-600).
                  statusFilter === f.value ? `bg-primary-600 border-primary-600` : `border-gray-200 bg-white`,
                )}
              >
                <Text style={tw.style(`text-sm`, statusFilter === f.value ? `text-white font-500` : `text-gray-500`)}>
                  {f.label}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </SafeAreaView>

      {/* Card de contenido: ocupa el alto disponible con incidencias; sin nada (o cargando)
          se queda chica para no llegar al botón flotante. */}
      <View style={tw.style(
        `mx-4 bg-white border border-gray-200 rounded-2xl overflow-hidden`,
        tickets.length > 0 && `flex-1 mb-4`,
      )}>
      {ticketsQuery.isLoading && tickets.length === 0 ? (
        <View style={tw`p-10 items-center`}>
          <ActivityIndicator color={tw.color('primary')} />
        </View>
      ) : tickets.length === 0 ? (
        <View style={tw`p-10 items-center`}>
          <Text style={tw`text-gray-400 text-center`}>
            {tab === 'assigned' ? 'No hay incidencias.' : 'No has reportado incidencias.'}
          </Text>
        </View>
      ) : (
        <FlatList
          style={tw`flex-1`}
          data={tickets}
          keyExtractor={(t) => t.uuid}
          contentContainerStyle={tw`pb-24`}
          refreshControl={
            <RefreshControl refreshing={ticketsQuery.isFetching} onRefresh={() => ticketsQuery.refetch()} />
          }
          renderItem={({ item }) => (
            <IncidentRow
              ticket={item}
              onPress={() => navigation.navigate('Guard.Tickets.Detail', {
                communityId,
                ticketUuid: item.uuid,
              })}
            />
          )}
        />
      )}
      </View>

      {/* Botón flotante, mismo estilo que "Nueva autorización" en Acceso. */}
      <TouchableOpacity
        onPress={() => setShowNew(true)}
        style={tw`absolute bottom-6 right-6 bg-primary px-5 py-3 rounded-full row items-center shadow-md`}
      >
        <SvgIcon name='add' size={16} color='white' />
        <Text style={tw`text-white font-500 ml-2`}>Nueva incidencia</Text>
      </TouchableOpacity>

      <NewTicketModal
        visible={showNew}
        communityId={communityId}
        onRequestClose={() => setShowNew(false)}
        onCreated={() => {
          setShowNew(false);
          ticketsQuery.refetch();
        }}
      />
    </View>
  );
}


// Igual que SubTabCard de Acceso: mismo tamaño seleccionada o no (borde de 1px siempre)
// y el contador visible en las dos.
function TabPill({ label, count, active, onPress }: any) {
  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(
        `flex-1 bg-white border border-gray-200 rounded-xl py-3 items-center`,
        active && `border-primary bg-primary/5`,
      )}
    >
      <Text style={tw.style(`text-2xl font-600 text-gray-500`, active && `text-primary`)}>{count}</Text>
      <Text style={tw.style(`text-sm text-gray-500`, active && `text-primary font-500`)}>{label}</Text>
    </TouchableOpacity>
  );
}


function IncidentRow({ ticket, onPress }: any) {
  const highPriority = !!ticket.is_high_priority;
  const parsed = parseLocation(ticket.description);
  const location = parsed.location ?? ticket.area?.name ?? null;
  // Sin título: la categoría dice qué pasó; si no hay, el inicio de la descripción.
  const heading = ticket.category?.name ?? parsed.body.split('\n')[0];
  const photoCount = ticket.files?.length ?? 0;

  return (
    <TouchableOpacity
      onPress={onPress}
      style={tw.style(
        // Renglón dentro de la card (como las autorizaciones en Acceso); prioridad alta con fondo rojo tenue.
        `px-4 py-3 border-b border-gray-100`,
        highPriority && `bg-red-50`,
      )}
    >
      <Text style={tw`text-base text-gray-900 font-500`} numberOfLines={1}>
        {highPriority && <Text style={tw`text-red-500`}>‼ </Text>}
        {heading}
      </Text>

      {location && (
        <Text style={tw`text-gray-500 text-sm mt-0.5`} numberOfLines={1}>{location}</Text>
      )}

      <View style={tw`row justify-between items-center mt-3`}>
        <View style={tw`row items-center`}>
          <SvgIcon name="image" color={tw.color('gray-400')} size={14} />
          <Text style={tw`text-xs text-gray-500 ml-1`}>
            {photoCount === 1 ? '1 foto' : `${photoCount} fotos`}
          </Text>
        </View>
        <Text style={tw`text-xs text-gray-400`}>
          {dayjs(ticket.created_at).isSame(dayjs(), 'day')
            ? `Hoy · ${dayjs(ticket.created_at).format('HH:mm')}`
            : dayjs(ticket.created_at).format('D/M/YY')}
        </Text>
      </View>
    </TouchableOpacity>
  );
}
